import { useInView } from '../../hooks/useInView';

interface AIBlockProps {
  eyebrow?: string;
  headline: string;
  lead: string;
  paragraphs: string[];
  tags: string[];
}

export default function SectionAIBlock({
  eyebrow = 'What We Do',
  headline,
  lead,
  paragraphs,
  tags,
}: AIBlockProps) {
  const { ref, inView } = useInView();

  return (
    <section className="bg-white py-28 md:py-36 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
          {/* Left: decorative column */}
          <div className="hidden lg:block lg:col-span-3">
            <div className="sticky top-32">
              <div
                className="text-[#0D1B2A]/5 font-serif leading-none select-none pointer-events-none"
                style={{ fontFamily: "'Playfair Display', serif", fontSize: '200px', lineHeight: 1 }}
              >
                &ldquo;
              </div>
            </div>
          </div>

          {/* Right: content */}
          <div
            ref={ref as React.RefObject<HTMLDivElement>}
            className={`lg:col-span-9 transition-all duration-700 ${
              inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="h-px w-10 bg-[#00B4D8]" />
              <span className="text-[#00B4D8] text-[11px] font-bold uppercase tracking-[0.25em]">
                {eyebrow}
              </span>
            </div>

            <h2
              className="text-4xl md:text-5xl font-bold text-[#0D1B2A] mb-10 leading-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {headline}
            </h2>

            <p className="text-xl md:text-2xl text-[#0D1B2A] leading-relaxed font-medium mb-8 border-l-4 border-[#00B4D8] pl-6">
              {lead}
            </p>

            <div className="w-14 h-px bg-[#00B4D8]/40 mb-8" />

            <div className="space-y-5">
              {paragraphs.map((p, i) => (
                <p key={i} className="text-[#57697A] text-lg leading-relaxed">
                  {p}
                </p>
              ))}
            </div>

            {tags.length > 0 && (
              <div className="flex flex-wrap gap-2.5 mt-10 pt-8 border-t border-[#E8ECF0]">
                {tags.map((tag) => (
                  <span
                    key={tag}
                    className="bg-[#E8F8FC] text-[#007A94] text-[12px] font-semibold px-4 py-1.5 rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
